import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Briefcase, Hash, Globe, ChevronLeft, Check, Users, FileText, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import studyService from '../../services/studyService';

const RequiredFieldMarker = () => (
  <span className="text-red-500 ml-1" aria-label="required field">
    *
  </span>
);

const EditStudy = () => {
  const { studyID } = useParams();
  const navigate = useNavigate();
  const [study, setStudy] = useState({
    title: '',
    description: '',
    code: '',
    status: '',
    is_byod_allowed: false,
    min_participant_id: '',
    max_participant_id: '',
    participant_portal_title: '',
    participant_portal_description: '',
    participant_portal_show_study_code: true,
    allow_caregivers: false,
    // FECOA-1570: owned by the Study Builder's Clinic Visit Behavior section.
    // Still carried in this form's state so its PUT round-trips the value
    // rather than resetting it.
    tablet_surface_remote_daily: true,
    offline_cutoff_hours_override: '',
  });

  const [errors, setErrors] = useState({});
  // Resolved cutoff at load time (study override, else sponsor setting, else
  // no cutoff) — display context only, never submitted (FECOA-1270).
  const [effectiveOfflineCutoff, setEffectiveOfflineCutoff] = useState(null);

  const IsValidInteger = (value) => {
    if (Number.isInteger(value))
      return true;
    // Accept any string of digits, including leading-zero IDs (e.g. "00123")
    const regex = /^\d+$/;
    return regex.test(value);
  }

  useEffect(() => {
    const fetchStudyData = async () => {
      try {
        const data = await studyService.getSingleStudy(studyID);
        // Normalize null values to empty strings to prevent controlled input warnings
        setStudy({
          title: data.title || '',
          description: data.description || '',
          code: data.code || '',
          status: data.status || '',
          is_byod_allowed: data.is_byod_allowed || false,
          min_participant_id: data.min_participant_id != null ? String(data.min_participant_id) : '',
          max_participant_id: data.max_participant_id != null ? String(data.max_participant_id) : '',
          participant_portal_title: data.participant_portal_title || '',
          participant_portal_description: data.participant_portal_description || '',
          participant_portal_show_study_code: data.participant_portal_show_study_code !== false,
          allow_caregivers: data.allow_caregivers || false,
          // FECOA-1570: coalesce on null/undefined only. This form no longer
          // renders the toggle but still PUTs the field, so `|| false` would
          // silently disable a study whose response omitted it.
          tablet_surface_remote_daily: data.tablet_surface_remote_daily ?? true,
          offline_cutoff_hours_override: data.offline_cutoff_hours_override != null ? String(data.offline_cutoff_hours_override) : '',
        });
        setEffectiveOfflineCutoff(data.effective_offline_cutoff_hours ?? null);
      } catch (error) {
        console.error('Error fetching study data:', error);
        toast.error('Failed to load study data. Please try again.', { type: 'error' });
      }
    };
    fetchStudyData();
  }, [studyID]);

  const validateForm = () => {
    const newErrors = {};

    if (!study.title.trim()) {
      newErrors.title = 'Study title is required';
    }

    if (!study.code.trim()) {
      newErrors.code = 'Study code is required';
    }

    if (!study.status) {
      newErrors.status = 'Status is required';
    }

    if (study.min_participant_id != null && study.min_participant_id !== '') {
      const min = parseInt(study.min_participant_id);
      if (!IsValidInteger(study.min_participant_id)) {
        newErrors.min_participant_id = 'Min ID must be a valid integer';
      } else if (min < 1) {
        newErrors.min_participant_id = 'Min ID must be at least 1';
      }
    }

    if (study.max_participant_id != null && study.max_participant_id !== '') {
      const max = parseInt(study.max_participant_id);
      if (!IsValidInteger(study.max_participant_id)) {
        newErrors.max_participant_id = 'Max ID must be a valid integer';
      } else if (max < 1) {
        newErrors.max_participant_id = 'Max ID must be at least 1';
      }
    }

    if (study.min_participant_id != null && study.min_participant_id !== '' && study.max_participant_id != null && study.max_participant_id !== '') {
      const min = parseInt(study.min_participant_id);
      const max = parseInt(study.max_participant_id);
      
      if (min >= max) {
        newErrors.min_participant_id = 'Min ID must be less than Max ID';
        newErrors.max_participant_id = 'Max ID must be greater than Min ID';
      }
    }

    if (study.offline_cutoff_hours_override !== '' && !IsValidInteger(study.offline_cutoff_hours_override)) {
      newErrors.offline_cutoff_hours_override = 'Offline cutoff must be a whole number of hours (0 for no cutoff)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newErrors = { ...errors };
    
    if (type === 'checkbox') {
      setStudy(prevStudy => ({
        ...prevStudy,
        [name]: checked
      }));
    } else if (name === 'min_participant_id' || name === 'max_participant_id') {
      // Handle participant ID fields (min and max)
      if (value === '') {
        // Handle empty value
        setStudy(prevStudy => {
          const updatedStudy = {
            ...prevStudy,
            [name]: ''
          };
          
          // Clear errors when field is emptied
          delete newErrors[name];
          return updatedStudy;
        });
      } else {
        // Validate non-empty value
        if (!IsValidInteger(value) || parseInt(value) < 0) {
          newErrors[name] = `Must be a valid non-negative integer`;
          setStudy(prevStudy => ({
            ...prevStudy,
            [name]: value
          }));
        } else {
          const intValue = parseInt(value);
          
          // Basic validation for individual field
          if (intValue < 1) {
            newErrors[name] = `${name === 'min_participant_id' ? 'Min' : 'Max'} ID must be at least 1`;
          } else {
            delete newErrors[name];
          }
          
          // Update study state, preserving the raw entry so leading-zero IDs survive
          setStudy(prevStudy => {
            const updatedStudy = {
              ...prevStudy,
              [name]: value
            };
            
            // Validate min/max relationship if both fields have values
            const otherField = name === 'min_participant_id' ? 'max_participant_id' : 'min_participant_id';
            if (updatedStudy[otherField]) {
              const min = name === 'min_participant_id' ? intValue : parseInt(updatedStudy.min_participant_id);
              const max = name === 'max_participant_id' ? intValue : parseInt(updatedStudy.max_participant_id);
              
              // Check that min is strictly less than max
              if (min >= max) {
                newErrors.min_participant_id = 'Min ID must be less than Max ID';
                newErrors.max_participant_id = 'Max ID must be greater than Min ID';
              } else {
                // Only clear relationship errors, not any individual field errors
                if (!newErrors.min_participant_id?.includes('valid') && !newErrors.min_participant_id?.includes('at least')) {
                  delete newErrors.min_participant_id;
                }
                if (!newErrors.max_participant_id?.includes('valid') && !newErrors.max_participant_id?.includes('at least')) {
                  delete newErrors.max_participant_id;
                }
              }
            }
            
            return updatedStudy;
          });
        }
      }
    } else if (name === 'offline_cutoff_hours_override') {
      // Whole hours only; empty means inherit the sponsor setting
      setStudy(prevStudy => ({
        ...prevStudy,
        [name]: value
      }));
      if (value === '' || IsValidInteger(value)) {
        delete newErrors[name];
      } else {
        newErrors[name] = 'Offline cutoff must be a whole number of hours (0 for no cutoff)';
      }
    } else {
      // Handle all other fields
      setStudy(prevStudy => ({
        ...prevStudy,
        [name]: value
      }));
      // Clear the error for this field
      delete newErrors[name];
    }

    setErrors(newErrors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please correct all errors before submitting.');
      return;
    }

    try {
      const payload = {
        ...study,
        // PositiveIntegerField on the backend: blank inherits (null), digits are hours
        offline_cutoff_hours_override: study.offline_cutoff_hours_override === ''
          ? null
          : parseInt(study.offline_cutoff_hours_override, 10),
      };
      await studyService.updateStudy(studyID, payload);
      toast.success('Study updated successfully!', { type: 'success' });
      setTimeout(() => navigate(`/studies/${studyID}`), 1000);
    } catch (error) {
      console.error('Error updating study:', error);
      
      // Parse backend validation errors and display on form fields
      const responseData = error.response?.data;
      const responseErrors = responseData?.errors || responseData;
      
      if (responseErrors && typeof responseErrors === 'object') {
        const fieldErrors = {};
        
        // Handle generic error message that mentions duplicate code
        if (responseErrors.error && typeof responseErrors.error === 'string') {
          if (responseErrors.error.includes('code') || responseErrors.error.includes('duplicate key')) {
            fieldErrors.code = 'A study with this code already exists.';
          } else {
            toast.error(responseErrors.error);
            return;
          }
        } else {
          // Handle field-specific errors
          Object.entries(responseErrors).forEach(([field, messages]) => {
            if (field !== 'status' && field !== 'message') {
              fieldErrors[field] = Array.isArray(messages) ? messages[0] : messages;
            }
          });
        }
        
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          toast.error('Please correct the errors below.');
          return;
        }
      }
      
      toast.error('Failed to update study. Please try again.', { type: 'error' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <Link
            to={`/studies/${studyID}`}
            className="mb-4 flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            <span>Back to Study Details</span>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          <div className="px-6 py-4 bg-primary-600 dark:bg-primary-700">
            <h2 className="text-xl font-semibold text-white">Study Details</h2>
          </div>
          <div className="px-6 py-8 sm:p-10">
            <div className="mb-4 text-sm text-gray-700">
              Fields marked with <span className="text-red-500">*</span> are required
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Study Title
                  <RequiredFieldMarker />
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={study.title}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-500">{errors.title}</p>
                )}
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={study.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                    Study Code
                    <RequiredFieldMarker />
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      id="code"
                      name="code"
                      value={study.code}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.code ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.code && (
                    <p className="mt-1 text-sm text-red-500">{errors.code}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                    <RequiredFieldMarker />
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <select
                      id="status"
                      name="status"
                      value={study.status}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none ${
                        errors.status ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select status</option>
                      <option value="planned">Planned</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                  {errors.status && (
                    <p className="mt-1 text-sm text-red-500">{errors.status}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="min_participant_id" className="block text-sm font-medium text-gray-700 mb-1">
                    Min Participant ID
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      inputMode="numeric"
                      id="min_participant_id"
                      name="min_participant_id"
                      value={study.min_participant_id}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.min_participant_id ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.min_participant_id && (
                    <p className="mt-1 text-sm text-red-500">{errors.min_participant_id}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="max_participant_id" className="block text-sm font-medium text-gray-700 mb-1">
                    Max Participant ID
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      inputMode="numeric"
                      id="max_participant_id"
                      name="max_participant_id"
                      value={study.max_participant_id}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.max_participant_id ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.max_participant_id && (
                    <p className="mt-1 text-sm text-red-500">{errors.max_participant_id}</p>
                  )}
                </div>
              </div>

              {/* BYOD checkbox removed — device configuration now lives on the
                 Devices tab in Study Details (FECOA-698). The backend field
                 is_byod_allowed is kept in sync automatically from device_strategy. */}

              {/* Participant Portal Customization Section */}
              <div className="border-t border-gray-200 pt-6 mt-6">
                <div className="flex items-center mb-4">
                  <Users className="w-5 h-5 text-primary-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Participant Portal</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Customize how the participant login page appears for this study. Leave blank to use defaults.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="participant_portal_title" className="block text-sm font-medium text-gray-700 mb-1">
                      Portal Title
                    </label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        id="participant_portal_title"
                        name="participant_portal_title"
                        value={study.participant_portal_title}
                        onChange={handleInputChange}
                        placeholder="Participant Portal"
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Displayed as the main heading on the login page. Default: "Participant Portal"
                    </p>
                  </div>

                  <div>
                    <label htmlFor="participant_portal_description" className="block text-sm font-medium text-gray-700 mb-1">
                      Portal Description
                    </label>
                    <textarea
                      id="participant_portal_description"
                      name="participant_portal_description"
                      value={study.participant_portal_description}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Welcome message or study description for participants..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Optional description shown below the title on the participant login page.
                    </p>
                  </div>

                  {/* Show Study Code Toggle */}
                  <div className="flex items-center mt-4">
                    <label className="flex items-center cursor-pointer">
                      <div className="relative">
                        <input
                          type="checkbox"
                          id="participant_portal_show_study_code"
                          name="participant_portal_show_study_code"
                          checked={study.participant_portal_show_study_code}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className={`w-5 h-5 border-2 rounded-sm transition-colors duration-200 ease-in-out ${study.participant_portal_show_study_code ? 'bg-primary-500 border-primary-500' : 'border-gray-300'}`}>
                          <Check className={`w-4 h-4 text-white transition-opacity duration-200 ease-in-out ${study.participant_portal_show_study_code ? 'opacity-100' : 'opacity-0'}`} />
                        </div>
                      </div>
                      <span className="ml-2 text-sm text-gray-700">
                        Show study code on login page
                      </span>
                    </label>
                  </div>

                  {/* Live Preview */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-700 mb-3">Preview</p>
                    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                      <div className="max-w-sm mx-auto bg-white rounded-xl shadow-md p-6">
                        <div className="text-center">
                          <h2 className="text-xl font-bold text-gray-900">
                            {study.participant_portal_title || 'Participant Portal'}
                          </h2>
                          {study.participant_portal_show_study_code && (
                            <p className="mt-2 text-xs text-gray-600">
                              Study: <span className="font-semibold text-primary-700">{study.code?.toUpperCase() || 'STUDY-CODE'}</span>
                            </p>
                          )}
                          {study.participant_portal_description && (
                            <p className="mt-2 text-xs text-gray-600">
                              {study.participant_portal_description}
                            </p>
                          )}
                          <p className="mt-3 text-xs font-medium text-gray-700">
                            Sign in to your account
                          </p>
                        </div>
                        <div className="mt-4 space-y-3">
                          <div className="h-8 bg-gray-100 rounded border border-gray-200"></div>
                          <div className="h-8 bg-gray-100 rounded border border-gray-200"></div>
                          <div className="h-8 bg-primary-600 rounded"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Protocol Settings Section (FECOA-1332) */}
              <div className="border-t border-gray-200 pt-6 mt-6">
                <div className="flex items-center mb-4">
                  <Settings className="w-5 h-5 text-primary-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Protocol Settings</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Configure protocol-specific features for this study.
                </p>

                <div className="flex items-start justify-between gap-4 py-3">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Enable Caregiver Support</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Allow caregivers to be added to new and existing participants for Observer-Reported outcomes.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                    <input
                      type="checkbox"
                      id="allow_caregivers"
                      name="allow_caregivers"
                      checked={study.allow_caregivers}
                      onChange={handleInputChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>

                {/* FECOA-1270: offline cutoff override */}
                <div className="flex items-start justify-between gap-4 py-3 border-t border-gray-100">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Offline cutoff (hours)</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum time a device may stay offline before the app locks new offline answers for this study. Leave blank to inherit the sponsor setting, or enter 0 for no cutoff.
                      {' '}Currently in effect: {effectiveOfflineCutoff != null ? `${effectiveOfflineCutoff} hours` : 'no cutoff'}.
                    </p>
                  </div>
                  <div className="flex-shrink-0 w-28">
                    <input
                      type="text"
                      inputMode="numeric"
                      id="offline_cutoff_hours_override"
                      name="offline_cutoff_hours_override"
                      value={study.offline_cutoff_hours_override}
                      onChange={handleInputChange}
                      placeholder="Inherit"
                      className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.offline_cutoff_hours_override ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {errors.offline_cutoff_hours_override && (
                      <p className="mt-1 text-xs text-red-600">{errors.offline_cutoff_hours_override}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => navigate(`/studies/${studyID}`)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default EditStudy;